import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { 
  Plus, Edit, Trash2, GripVertical, Image as ImageIcon, 
  FileText, Video, X, Save, ExternalLink 
} from 'lucide-react';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable Item Component
const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners, isDragging })}
    </div>
  );
};

const GalleryManagement = ({ auth }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('images');
  const [images, setImages] = useState([]);
  const [articles, setArticles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [imagesRes, articlesRes, sessionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/gallery/images`),
        axios.get(`${BACKEND_URL}/api/gallery/articles`),
        axios.get(`${BACKEND_URL}/api/gallery/sessions`)
      ]);
      setImages(imagesRes.data);
      setArticles(articlesRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error('Error loading gallery data:', error);
      toast({
        title: "Error",
        description: "Failed to load gallery data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getEndpoint = (type) => {
    switch (type) {
      case 'images': return 'images';
      case 'articles': return 'articles';
      case 'sessions': return 'sessions';
      default: return 'images';
    }
  };

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'images': return images;
      case 'articles': return articles;
      case 'sessions': return sessions;
      default: return [];
    }
  };

  const setCurrentItems = (items) => {
    switch (activeTab) {
      case 'images': setImages(items); break;
      case 'articles': setArticles(items); break;
      case 'sessions': setSessions(items); break;
    }
  };

  const getDefaultFormData = () => {
    switch (activeTab) {
      case 'images':
        return { url: '', title: '', caption: '' };
      case 'articles':
        return { title: '', publication: '', date: '', url: '', description: '' };
      case 'sessions':
        return { title: '', platform: '', description: '', url: '', thumbnail: '', video_id: '' };
      default:
        return {};
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(getDefaultFormData());
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/gallery/${getEndpoint(activeTab)}/${itemId}`,
        { headers: { 'Authorization': `Basic ${auth}` } }
      );
      toast({ title: "Success", description: "Item deleted successfully" });
      loadAllData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete item",
        variant: "destructive"
      });
    }
  };

  const handleSave = async () => {
    try {
      const endpoint = getEndpoint(activeTab);
      const items = getCurrentItems();
      const order = editingItem ? editingItem.order : items.length;

      const payload = { ...formData, order };

      if (editingItem) {
        await axios.put(
          `${BACKEND_URL}/api/gallery/${endpoint}/${editingItem.id}`,
          payload,
          { headers: { 'Authorization': `Basic ${auth}` } }
        );
        toast({ title: "Success", description: "Item updated successfully" });
      } else {
        await axios.post(
          `${BACKEND_URL}/api/gallery/${endpoint}`,
          payload,
          { headers: { 'Authorization': `Basic ${auth}` } }
        );
        toast({ title: "Success", description: "Item added successfully" });
      }

      setShowForm(false);
      setEditingItem(null);
      setFormData({});
      loadAllData();
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save item",
        variant: "destructive"
      });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const items = getCurrentItems();
    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = arrayMove(items, oldIndex, newIndex);
    setCurrentItems(newItems);

    // Update order in backend
    try {
      const endpoint = getEndpoint(activeTab);
      const updatePromises = newItems.map((item, index) =>
        axios.put(
          `${BACKEND_URL}/api/gallery/${endpoint}/${item.id}`,
          { order: index },
          { headers: { 'Authorization': `Basic ${auth}` } }
        )
      );
      await Promise.all(updatePromises);
      toast({ title: "Success", description: "Order updated successfully" });
    } catch (error) {
      console.error('Reorder error:', error);
      toast({
        title: "Error",
        description: "Failed to update order",
        variant: "destructive"
      });
      loadAllData(); // Reload to restore original order
    }
  };

  const renderForm = () => {
    if (activeTab === 'images') {
      return (
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Image URL *</label>
            <Input
              value={formData.url || ''}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Title *</label>
            <Input
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Image title"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Caption</label>
            <Textarea
              value={formData.caption || ''}
              onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
              placeholder="Optional description"
              rows={2}
            />
          </div>
          {formData.url && (
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium">Preview</label>
              <img 
                src={formData.url} 
                alt="Preview" 
                className="max-w-xs h-32 object-cover rounded border"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'articles') {
      return (
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Title *</label>
            <Input
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Article title"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium">Publication *</label>
              <Input
                value={formData.publication || ''}
                onChange={(e) => setFormData({ ...formData, publication: e.target.value })}
                placeholder="e.g., LinkedIn, Medium"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Date *</label>
              <Input
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                placeholder="e.g., March 2024"
              />
            </div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Article URL *</label>
            <Input
              value={formData.url || ''}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://linkedin.com/article/..."
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Description *</label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief summary of the article"
              rows={3}
            />
          </div>
        </div>
      );
    }

    if (activeTab === 'sessions') {
      return (
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Title *</label>
            <Input
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Session title"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Platform *</label>
            <Input
              value={formData.platform || ''}
              onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
              placeholder="e.g., YouTube, LinkedIn Live"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Video URL *</label>
            <Input
              value={formData.url || ''}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Thumbnail URL</label>
            <Input
              value={formData.thumbnail || ''}
              onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
              placeholder="https://example.com/thumbnail.jpg"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Video ID</label>
            <Input
              value={formData.video_id || ''}
              onChange={(e) => setFormData({ ...formData, video_id: e.target.value })}
              placeholder="YouTube video ID (optional)"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Description *</label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the session"
              rows={3}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const renderItemCard = (item, { attributes, listeners, isDragging }) => {
    if (activeTab === 'images') {
      return (
        <Card className={`mb-3 ${isDragging ? 'shadow-lg' : ''}`}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-4">
              <div 
                {...attributes} 
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded flex-shrink-0"
              >
                <GripVertical className="h-4 w-4 md:h-5 md:w-5 text-slate-400" />
              </div>
              <img 
                src={item.url} 
                alt={item.title}
                className="h-12 w-16 md:h-16 md:w-24 object-cover rounded flex-shrink-0"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/96x64?text=No+Image';
                }}
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium truncate text-sm md:text-base">{item.title}</h4>
                <p className="text-xs md:text-sm text-slate-500 truncate">{item.caption}</p>
              </div>
              <div className="flex gap-1 md:gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => handleEdit(item)} className="p-2">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} className="p-2">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (activeTab === 'articles') {
      return (
        <Card className={`mb-3 ${isDragging ? 'shadow-lg' : ''}`}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-4">
              <div 
                {...attributes} 
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded flex-shrink-0"
              >
                <GripVertical className="h-4 w-4 md:h-5 md:w-5 text-slate-400" />
              </div>
              <div className="h-12 w-12 md:h-16 md:w-16 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                <FileText className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium truncate text-sm md:text-base">{item.title}</h4>
                <div className="flex flex-wrap items-center gap-1 md:gap-2 text-xs md:text-sm text-slate-500">
                  <Badge variant="outline" className="text-xs">{item.publication}</Badge>
                  <span className="hidden sm:inline">{item.date}</span>
                </div>
              </div>
              <div className="flex gap-1 md:gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => window.open(item.url, '_blank')} className="p-2">
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleEdit(item)} className="p-2">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} className="p-2">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (activeTab === 'sessions') {
      return (
        <Card className={`mb-3 ${isDragging ? 'shadow-lg' : ''}`}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-4">
              <div 
                {...attributes} 
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded flex-shrink-0"
              >
                <GripVertical className="h-4 w-4 md:h-5 md:w-5 text-slate-400" />
              </div>
              {item.thumbnail ? (
                <img 
                  src={item.thumbnail} 
                  alt={item.title}
                  className="h-12 w-16 md:h-16 md:w-24 object-cover rounded flex-shrink-0"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/96x64?text=Video';
                  }}
                />
              ) : (
                <div className="h-12 w-16 md:h-16 md:w-24 bg-purple-100 rounded flex items-center justify-center flex-shrink-0">
                  <Video className="h-6 w-6 md:h-8 md:w-8 text-purple-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium truncate text-sm md:text-base">{item.title}</h4>
                <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-slate-500">
                  <Badge variant="outline" className="text-xs">{item.platform}</Badge>
                </div>
              </div>
              <div className="flex gap-1 md:gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => window.open(item.url, '_blank')} className="p-2">
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleEdit(item)} className="p-2">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} className="p-2">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-500">Loading gallery data...</div>
      </div>
    );
  }

  const items = getCurrentItems();

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl md:text-2xl font-bold">Gallery Management</h2>
        <Button onClick={handleAdd} className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Add {activeTab === 'images' ? 'Image' : activeTab === 'articles' ? 'Article' : 'Session'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-3 md:gap-6 grid-cols-3">
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-center gap-2 md:gap-3">
              <ImageIcon className="h-6 w-6 md:h-8 md:w-8 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-xs md:text-sm text-slate-600">Images</p>
                <p className="text-xl md:text-3xl font-bold text-green-900">{images.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-center gap-2 md:gap-3">
              <FileText className="h-6 w-6 md:h-8 md:w-8 text-blue-600 flex-shrink-0" />
              <div>
                <p className="text-xs md:text-sm text-slate-600">Articles</p>
                <p className="text-xl md:text-3xl font-bold text-blue-900">{articles.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-center gap-2 md:gap-3">
              <Video className="h-6 w-6 md:h-8 md:w-8 text-purple-600 flex-shrink-0" />
              <div>
                <p className="text-xs md:text-sm text-slate-600">Sessions</p>
                <p className="text-xl md:text-3xl font-bold text-purple-900">{sessions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 md:gap-2 border-b pb-2 overflow-x-auto">
        <Button
          variant={activeTab === 'images' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('images')}
          className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap"
          size="sm"
        >
          <ImageIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Images</span> ({images.length})
        </Button>
        <Button
          variant={activeTab === 'articles' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('articles')}
          className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap"
          size="sm"
        >
          <FileText className="h-4 w-4" />
          <span className="hidden sm:inline">Articles</span> ({articles.length})
        </Button>
        <Button
          variant={activeTab === 'sessions' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('sessions')}
          className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap"
          size="sm"
        >
          <Video className="h-4 w-4" />
          <span className="hidden sm:inline">Sessions</span> ({sessions.length})
        </Button>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <Card className="border-2 border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-4 p-3 md:p-6">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base md:text-lg">
                {editingItem ? 'Edit' : 'Add New'} {activeTab === 'images' ? 'Image' : activeTab === 'articles' ? 'Article' : 'Session'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingItem(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 p-3 md:p-6 pt-0">
            {renderForm()}
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700 flex-1 sm:flex-none">
                <Save className="h-4 w-4 mr-2" />
                {editingItem ? 'Update' : 'Add'}
              </Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingItem(null); }} className="flex-1 sm:flex-none">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items List with Drag and Drop */}
      <div className="space-y-2">
        <p className="text-sm text-slate-500 flex items-center gap-2">
          <GripVertical className="h-4 w-4" />
          Drag items to reorder
        </p>
        
        {items.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center text-slate-500">
              <div className="mb-4">
                {activeTab === 'images' && <ImageIcon className="h-12 w-12 mx-auto opacity-50" />}
                {activeTab === 'articles' && <FileText className="h-12 w-12 mx-auto opacity-50" />}
                {activeTab === 'sessions' && <Video className="h-12 w-12 mx-auto opacity-50" />}
              </div>
              <p>No {activeTab} yet. Click the button above to add one.</p>
            </CardContent>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item) => (
                <SortableItem key={item.id} id={item.id}>
                  {(sortableProps) => renderItemCard(item, sortableProps)}
                </SortableItem>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default GalleryManagement;
